import React from 'react';
import { Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getWorkspaceTokens } from './workspaceStyles';

const RATIO_ROWS = {
  '60/40': 'minmax(340px, 60fr) minmax(220px, 40fr)',
  '55/45': 'minmax(320px, 55fr) minmax(240px, 45fr)',
  '44/56': 'minmax(300px, 44fr) minmax(260px, 56fr)',
  chartTable: 'minmax(320px, 44fr) minmax(260px, 56fr)',
  primaryChart: 'minmax(360px, 60fr) minmax(220px, 40fr)',
  even: 'minmax(280px, 1fr) minmax(280px, 1fr)',
};

const FLUID_RATIO_ROWS = {
  '60/40': 'minmax(300px, 1.15fr) minmax(280px, 0.85fr)',
  '55/45': 'minmax(300px, 1.05fr) minmax(280px, 0.95fr)',
  '44/56': 'minmax(280px, 0.9fr) minmax(280px, 1.1fr)',
  chartTable: 'minmax(300px, 0.95fr) minmax(280px, 1.05fr)',
  primaryChart: 'minmax(300px, 1.15fr) minmax(280px, 0.85fr)',
  even: 'minmax(280px, 1fr) minmax(280px, 1fr)',
};

function AnalyticsSplitLayout({
  children,
  enabled = true,
  ratio = '60/40',
  divider = true,
  sizing = 'fixed',
  orientation = 'vertical',
  stackBelow = 'md',
  sx,
  style,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const childArray = React.Children.toArray(children).filter(Boolean);

  if (!enabled) {
    if (!sx && !style) return <>{children}</>;

    return (
      <Box style={style} sx={sx}>
        {children}
      </Box>
    );
  }

  const [primary, secondary] = childArray;
  const workspaceRatios = theme.workspace?.splitRatios || {};
  const workspaceFluidRatios = theme.workspace?.fluidSplitRatios || {};
  const fluidSplit = sizing === 'fluidSplit';
  const gridTemplateRows = fluidSplit
    ? workspaceFluidRatios[ratio] || FLUID_RATIO_ROWS[ratio] || FLUID_RATIO_ROWS.chartTable
    : workspaceRatios[ratio] || RATIO_ROWS[ratio] || RATIO_ROWS['60/40'];
  const horizontal = orientation === 'horizontal';
  const contentSized = sizing === 'content';
  const columnTemplate = ratio === 'primaryChart' ? 'minmax(0, 7fr) minmax(240px, 3fr)' : 'minmax(0, 1fr) minmax(0, 1fr)';
  const panelScroll = sizing === 'panelScroll';
  const managedViewport = panelScroll || fluidSplit;
  const viewportHeightSx = managedViewport
    ? { xs: 'auto', [stackBelow]: '100%' }
    : '100%';
  const viewportMaxHeightSx = managedViewport
    ? { xs: 'none', [stackBelow]: '100%' }
    : '100%';
  const panelOverflowSx = managedViewport
    ? { xs: 'visible', [stackBelow]: 'hidden' }
    : { xs: 'auto', [stackBelow]: 'hidden' };
  const rootOverflowSx = fluidSplit
    ? {
      overflowX: 'hidden',
      overflowY: { xs: 'visible', [stackBelow]: 'auto' },
    }
    : {
      overflow: panelOverflowSx,
    };
  const childHeightSx = contentSized ? 'auto' : managedViewport
    ? { xs: 'auto', [stackBelow]: '100%' }
    : '100%';
  const childMaxHeightSx = contentSized ? 'none' : managedViewport
    ? { xs: 'none', [stackBelow]: '100%' }
    : '100%';
  const mobileRows = fluidSplit
    ? 'minmax(320px, auto) minmax(360px, auto)'
    : 'minmax(300px, auto) minmax(260px, auto)';

  return (
    <Box
      data-testid="analytics-split-layout"
      data-ratio={ratio}
      data-sizing={sizing}
      data-orientation={orientation}
      data-row-template={gridTemplateRows}
      data-mobile-row-template={mobileRows}
      style={style}
      sx={{
        width: '100%',
        minWidth: 0,
        minHeight: managedViewport ? { xs: '100%', [stackBelow]: 0 } : 0,
        height: contentSized ? 'auto' : viewportHeightSx,
        maxHeight: contentSized ? 'none' : viewportMaxHeightSx,
        flex: contentSized ? '0 0 auto' : '1 1 0',
        display: 'grid',
        gridTemplateRows: contentSized ? (horizontal ? { xs: 'auto auto', [stackBelow]: 'auto' } : 'auto auto') : { xs: mobileRows, [stackBelow]: gridTemplateRows },
        gridTemplateColumns: horizontal ? { xs: 'minmax(0, 1fr)', [stackBelow]: columnTemplate } : undefined,
        gap: tokens.gap.compact,
        alignItems: 'stretch',
        ...(contentSized ? { overflow: 'visible' } : rootOverflowSx),
        ...sx,
      }}
    >
      {contentSized ? <>
        <Box sx={{ minWidth: 0 }}>{primary}</Box>
        <Box sx={{ minWidth: 0 }}>
          {divider && !horizontal && <Divider sx={{ mb: tokens.gap.compact }} />}
          {secondary}
        </Box>
      </> : <>
      <Box sx={{ minWidth: 0, minHeight: 0, height: childHeightSx, maxHeight: childMaxHeightSx, overflow: contentSized ? 'visible' : managedViewport ? { xs: 'visible', [stackBelow]: 'hidden' } : 'hidden' }}>
        {primary}
      </Box>
      <Box sx={{ minWidth: 0, minHeight: 0, height: childHeightSx, maxHeight: childMaxHeightSx, display: 'flex', flexDirection: 'column', overflow: contentSized ? 'visible' : managedViewport ? { xs: 'visible', [stackBelow]: 'hidden' } : 'hidden' }}>
        {divider && !horizontal && (
          <Divider sx={{ mb: tokens.gap.compact, width: '100%', alignSelf: 'center' }} />
        )}
        <Box sx={{
          minWidth: 0,
          minHeight: 0,
          height: childHeightSx,
          maxHeight: childMaxHeightSx,
          flex: '1 1 0',
          display: 'flex',
          flexDirection: 'column',
          overflow: contentSized ? 'visible' : managedViewport ? { xs: 'visible', [stackBelow]: 'hidden' } : 'hidden',
        }}>
          {secondary}
        </Box>
      </Box>
      </>}
    </Box>
  );
}

export default AnalyticsSplitLayout;
